import { Box, Button, DialogActions, Divider, IconButton, useMediaQuery, useTheme } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useFormikContext } from 'formik';
import { RaptorJobFormValues } from '../../../RaptorJobValidationSchema';
import tid from '@/common/infrastructure/config/tid';
import { JOB_FORM_STEPS, type JobFormStepType } from '@/raptorjob/domain/constants/jobFormLabels';

interface CreateModalDialogActionsProps {
  step: JobFormStepType;
  setStep: (step: JobFormStepType) => void;
  handleClose: () => void;
  onSubmit?: () => Promise<void>;
}

export const CreateModalDialogActions = ({ step, setStep, handleClose, onSubmit }: CreateModalDialogActionsProps) => {
  const { values, isValid, dirty, isSubmitting } = useFormikContext<RaptorJobFormValues>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <DialogActions sx={{ padding: 0 }}>
      <Box width={'100%'}>
        <Divider />
        <Box
          sx={{
            padding: { xs: 2, sm: '20px 24px' },
            display: 'flex',
            justifyContent: step === JOB_FORM_STEPS.JOB_INFO ? 'flex-end' : 'space-between'
          }}
        >
          {step === JOB_FORM_STEPS.RESOURCES && (
            <>
              {isMobile ? (
                <Box>
                  <IconButton
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => setStep(JOB_FORM_STEPS.JOB_INFO)}
                    color="primary"
                    {...tid('jobs:create:previous-button')}
                  >
                    <KeyboardArrowLeftIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  onClick={() => setStep(JOB_FORM_STEPS.JOB_INFO)}
                  disabled={isSubmitting}
                  variant="text"
                  startIcon={<KeyboardArrowLeftIcon />}
                  {...tid('jobs:create:previous-button')}
                >
                  PREVIOUS
                </Button>
              )}
            </>
          )}
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Button onClick={handleClose} disabled={isSubmitting} variant="text" {...tid('jobs:create:cancel-button')}>
              CANCEL
            </Button>
            <Button
              variant={values.recurrence === 'multi' || step === JOB_FORM_STEPS.RESOURCES ? 'contained' : 'outlined'}
              disabled={!isValid || !dirty || isSubmitting}
              color="primary"
              type="button"
              onClick={onSubmit}
              loading={isSubmitting}
              {...tid(step === JOB_FORM_STEPS.JOB_INFO ? 'jobs:create:create-job-button' : 'jobs:create:create-job-ticket-button')}
            >
              {step === JOB_FORM_STEPS.JOB_INFO && 'CREATE JOB'}
              {step === JOB_FORM_STEPS.RESOURCES && 'CREATE JOB & JOB TICKET'}
            </Button>
            {values.recurrence === 'single' && step === JOB_FORM_STEPS.JOB_INFO && (
              <Button
                variant="contained"
                color="primary"
                disabled={!isValid || !dirty || isSubmitting}
                onClick={() => {
                  setStep(JOB_FORM_STEPS.RESOURCES);
                }}
                {...tid('jobs:create:continue-to-resources-button')}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  CONTINUE TO RESOURCES
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  CONTINUE
                </Box>
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </DialogActions>
  );
};
